import { getSortedPostsData } from "utils/posts";
import Footer from "../components/frontpage/footer";
import Header from "../components/frontpage/header";
import RecentArticles from "../components/frontpage/recentArticles";
import { GetStaticProps } from "next";
import { PostData } from "pages/docs/[id]";
import { MailIcon, PhoneIcon, BookmarkAltIcon } from "@heroicons/react/outline";

const supportLinks = [
  {
    name: "Email us",
    href: "mailto:support@symbo.la",
    description:
      "Contact support@symbo.la with any questions and we'll be glad to help.",
    icon: MailIcon,
  },
  {
    name: "Call us",
    href: "/app",
    description:
      "Schedule a call with our support team from inside your Symbola dashboard (login required).",
    icon: PhoneIcon,
  },
  {
    name: "Documentation",
    href: "#documentation", //NOTE:  This link to a small first batch of articles is a temporary solution until a full documentation handbook is written.
    description:
      "Learn more about topics important to your growing startup and make informed decisions as you incorporate with our tools.",
    icon: BookmarkAltIcon,
  },
];

interface SupportProps {
  allPostsData: PostData[];
}

const Support: React.FC<SupportProps> = ({ allPostsData }) => {
  return (
    <div className="relative overflow-hidden">
      <Header lightMode={false} />

      <div className="bg-white">
        {/* Header */}
        <div className="relative pb-32 bg-gray-900">
          <div className="relative max-w-7xl mx-auto py-16  px-4  sm:py-24 sm:px-6 lg:px-8">
            <h1 className="text-4xl font-extrabold tracking-tight text-white md:text-5xl lg:text-6xl">
              Support
            </h1>
            <p className="mt-6 max-w-3xl text-xl text-gray-300">
              We&apos;re here to help. All of our tools for startup founders
              include full live support by phone, chat, or email.
            </p>
          </div>
        </div>

        {/* Overlapping cards */}
        <section
          className="-mt-32 max-w-7xl mx-auto relative z-10 pb-32 px-4 sm:px-6 lg:px-8"
          aria-labelledby="contact-heading"
        >
          <h2 className="sr-only" id="contact-heading">
            Contact us
          </h2>
          <div className="grid grid-cols-1 gap-y-20 lg:grid-cols-3 lg:gap-y-0 lg:gap-x-8">
            {supportLinks.map((link) => (
              <div
                key={link.name}
                className="flex flex-col bg-white rounded-2xl shadow-xl"
              >
                <div className="flex-1 relative pt-16 px-6 pb-8 md:px-8">
                  <div className="absolute top-0 p-5 inline-block bg-blue-600 rounded-xl shadow-lg transform -translate-y-1/2">
                    <link.icon
                      className="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </div>
                  <h3 className="text-xl font-medium text-gray-900">
                    {link.name}
                  </h3>
                  <p className="mt-4 text-base text-gray-500">
                    {link.description}
                  </p>
                </div>
                <div className="p-6 bg-gray-50 rounded-bl-2xl rounded-br-2xl md:px-8">
                  <a
                    href={link.href}
                    className="text-base font-medium text-blue-700 hover:text-blue-600"
                  >
                    {link.name}
                    <span aria-hidden="true"> &rarr;</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>
        <div id="documentation">
          <RecentArticles allPostsData={allPostsData} />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const allPostsData = getSortedPostsData();

  return {
    props: {
      allPostsData,
    },
  };
};

export default Support;
